import React, { useState, useEffect } from "react";
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TableRow,
  Table,
  TableHead,
  TableBody,
  TableCell,
  TableContainer,
  Paper,
  IconButton,
} from "@material-ui/core";
import SoundMasterService from "./Locality/Service/soundMasterService";
import * as Yup from 'yup';
import TablePagination from "@material-ui/core/TablePagination";
import { Grid, TextField } from "@material-ui/core";
import { useFormik } from 'formik';
import DeleteIcon from '@material-ui/icons/Delete';
import EditIcon from '@material-ui/icons/Edit';
import Widget from "../../components/Widget/Widget";
import PageTitle from "../../components/PageTitle/PageTitle";
import { withStyles, makeStyles } from "@material-ui/core/styles";

const StyledTableCell = withStyles((theme) => ({
  head: {
    backgroundColor: "#0f3b62ad",
    color: theme.palette.common.white,
  },
  body: {
    fontSize: 14,
  },
}))(TableCell);

const useStyles = makeStyles((theme) => ({
  tableOverflow: {
    overflowX: "auto",
  },
  container: {
    display: 'flex',
    flexWrap: 'wrap',
  },
  textField: {
    marginLeft: theme.spacing(1),
    marginRight: theme.spacing(1),
    width: 200,
  },
}));

export default function SoundMaster() {
  const tableHeaders = ['Sound Name', 'Edit', 'Delete'];
  const classes = useStyles();
  const [soundMasterList, setSoundMasterList] = useState([]);
  const [pg, setPg] = useState(0);
  const [rpg, setRpg] = useState(10);
  const [open, setOpen] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [error, setError] = useState(null);

  const [soundMaster, setSoundMaster] = useState({
    name: '',
    schooleId: '',
  });

  const validationSchema = Yup.object().shape({
    name: Yup.string().required('Sound Name is required'),
    schooleId: Yup.string(),
  });

  const formik = useFormik({
    initialValues: soundMaster,
    validationSchema: validationSchema,
    enableReinitialize: true,
    onSubmit: (values) => {
      if (isEditMode) {
        updateSoundMaster();
      } else {
        addSoundMaster();
      }
    },
  });

  useEffect(() => {
    getSoundMasterList();
    return () => {
      setSoundMasterList([]);
    };
  }, []);

  const getSoundMasterList = () => {
    const userDetails = JSON.parse(localStorage.getItem("userDetail") || "{}");
    const body = {};
    
    if (userDetails.schooleId) {
      body.schooleId = userDetails.schooleId;
    }
    
    SoundMasterService.findSoundMasterList(body)
      .then((res) => {
        setSoundMasterList(res);
      })
      .catch((err) => {
        console.error("Error fetching sound master list:", err);
        setError(err.message);
      });
  };


  const handleChangePage = (event, newPage) => {
    setPg(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRpg(parseInt(event.target.value, 10));
    setPg(0);
  };

  const handleOpen = (status) => {
    if (status) {
      const userDetails = JSON.parse(localStorage.getItem("userDetail") || "{}");
      const resetData = {
        name: '',
        schooleId: userDetails.schooleId || '',
      };
      setSoundMaster(resetData);
      setIsEditMode(false);
      // Reset Formik form to clear previous data
      formik.resetForm({
        values: resetData
      });
    }
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
    setIsEditMode(false);
  };


  const addSoundMaster = async () => {
    try {
      const userDetails = JSON.parse(localStorage.getItem("userDetail") || "{}");
      const body = {
        name: formik.values.name,
        schooleId: userDetails.schooleId || formik.values.schooleId,
      };

      await SoundMasterService.createSoundMaster(body);
      alert("Sound Master added successfully!");
      handleClose();
      // Refresh the list after a short delay to ensure state is updated
      setTimeout(() => {
        getSoundMasterList();
      }, 100);
    } catch (err) {
      console.error("Error adding sound master:", err);
      alert(err.response?.data?.message || "Error adding sound master");
    }
  };

  // Helper function to check if user is superAdmin
  const isSuperAdmin = () => {
    const userDetails = JSON.parse(localStorage.getItem("userDetail") || "{}");
    return userDetails.role === "superAdmin" || userDetails.role === "SUPERADMIN";
  };

  // Helper function to check if schooleId exists and is valid
  const hasSchooleId = (item) => {
    if (!item) return false;
    const schooleId = item.schooleId;
    // Check if schooleId exists and is not null/undefined/empty
    // Handle both ObjectId strings and populated objects
    if (schooleId === null || schooleId === undefined || schooleId === '') {
      return false;
    }
    // If it's an object (populated), check if it has an _id
    if (typeof schooleId === 'object' && schooleId._id) {
      return true;
    }
    // If it's a string (ObjectId), it's valid
    if (typeof schooleId === 'string' && schooleId.length > 0) {
      return true;
    }
    return false;
  };

  // Helper function to check if edit/delete is allowed
  const canEditOrDelete = (item) => {
    // SuperAdmin can edit/delete any record (even with null schooleId)
    if (isSuperAdmin()) {
      return true;
    }
    // Other users can only edit/delete records with schooleId
    return hasSchooleId(item);
  };

  const editSoundMaster = (soundMasterData) => {
    // Only allow editing if schooleId exists or user is superAdmin
    if (!canEditOrDelete(soundMasterData)) {
      alert("Cannot edit global records. Only school-specific records can be edited.");
      return;
    }

    const obj = {
      _id: soundMasterData._id,
      name: soundMasterData.name || '',
      schooleId: soundMasterData.schooleId?._id || soundMasterData.schooleId || '',
    };

    setSoundMaster(obj);
    setIsEditMode(true);
    setOpen(true);
  };

  const updateSoundMaster = async () => {
    try {
      const userDetails = JSON.parse(localStorage.getItem("userDetail") || "{}");
      const body = {
        name: formik.values.name,
        schooleId: userDetails.schooleId || formik.values.schooleId,
      };

      await SoundMasterService.updateSoundMaster(soundMaster._id, body);
      alert("Sound Master updated successfully!");
      handleClose();
      // Refresh the list after a short delay to ensure state is updated
      setTimeout(() => {
        getSoundMasterList();
      }, 100);
    } catch (err) {
      console.error("Error updating sound master:", err);
      alert(err.response?.data?.message || "Error updating sound master");
    }
  };

  const deleteSoundMaster = (soundMasterDelete) => {
    if (!soundMasterDelete) {
      return;
    }

    // Only allow deleting if schooleId exists or user is superAdmin
    if (!canEditOrDelete(soundMasterDelete)) {
      alert("Cannot delete global records. Only school-specific records can be deleted.");
      return;
    }

    const confirmDelete = window.confirm(
      "Are you sure you want to delete this Sound Master record?"
    );
    
    if (!confirmDelete) {
      return;
    }

    SoundMasterService.deleteSoundMaster(soundMasterDelete._id)
      .then((res) => {
        alert("Sound Master deleted successfully!");
        getSoundMasterList();
      })
      .catch((err) => {
        console.error("Error deleting sound master:", err);
        alert(err.response?.data?.message || "Error deleting sound master");
      });
  };


  return (
    <>
      <Grid container spacing={2}>
        <Grid item xs={10}>
          <PageTitle title="Sound Master" />
        </Grid>
        <Grid item xs={2}>
          <Button
            variant="contained"
            color="primary"
            onClick={() => handleOpen(true)}
            style={{ marginTop: '10px' }}
          >
            Add Sound Master
          </Button>
        </Grid>
      </Grid>

      <Grid container spacing={4}>
        <Grid item xs={12}>
          <Widget
            title="Sound Master List"
            upperTitle
            noBodyPadding
            bodyClass={classes.tableOverflow}
          >
            <TableContainer component={Paper} style={{ maxHeight: 500 }}>
              <Table stickyHeader className="mb-0">
                <TableHead>
                  <TableRow>
                    {tableHeaders.map((header) => (
                      <StyledTableCell key={header}>{header}</StyledTableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {soundMasterList
                    .slice(pg * rpg, pg * rpg + rpg)
                    .map((soundMasterItem) => (
                      <TableRow key={soundMasterItem._id}>
                        <TableCell className="pl-3 fw-normal">
                          {soundMasterItem.name || 'N/A'}
                        </TableCell>
                        <TableCell className="pl-3 fw-normal">
                          <IconButton
                            color="primary"
                            onClick={() => editSoundMaster(soundMasterItem)}
                            aria-label="edit"
                            disabled={!canEditOrDelete(soundMasterItem)}
                            style={{ 
                              opacity: canEditOrDelete(soundMasterItem) ? 1 : 0.5,
                              cursor: canEditOrDelete(soundMasterItem) ? 'pointer' : 'not-allowed'
                            }}
                          >
                            <EditIcon />
                          </IconButton>
                        </TableCell>
                        <TableCell className="pl-3 fw-normal">
                          <IconButton
                            color="secondary"
                            onClick={() => deleteSoundMaster(soundMasterItem)}
                            aria-label="delete"
                            disabled={!canEditOrDelete(soundMasterItem)}
                            style={{ 
                              opacity: canEditOrDelete(soundMasterItem) ? 1 : 0.5,
                              cursor: canEditOrDelete(soundMasterItem) ? 'pointer' : 'not-allowed'
                            }}
                          >
                            <DeleteIcon />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    ))}
                </TableBody>
              </Table>
            </TableContainer>

            <TablePagination
              component="div"
              rowsPerPageOptions={[5, 10, 25, 50, 100]}
              count={soundMasterList.length}
              page={pg}
              onPageChange={handleChangePage}
              rowsPerPage={rpg}
              onRowsPerPageChange={handleChangeRowsPerPage}
            />
          </Widget>
        </Grid>
      </Grid>

      {/* Add/Edit Dialog */}
      <Dialog
        open={open}
        onClose={handleClose}
        aria-labelledby="form-dialog-title"
        maxWidth="sm"
        scroll="paper"
        PaperProps={{
          style: {
            overflow: 'hidden'
          }
        }}
      >
        <DialogTitle id="form-dialog-title">
          {isEditMode ? "Edit Sound Master" : "Add Sound Master"}
        </DialogTitle>
        <form onSubmit={formik.handleSubmit}>
          <DialogContent>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  autoFocus
                  margin="normal"
                  id="name"
                  name="name"
                  label="Sound Name"
                  type="text"
                  variant="outlined"
                  value={formik.values.name}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  error={formik.touched.name && Boolean(formik.errors.name)}
                  helperText={formik.touched.name && formik.errors.name}
                  required
                />
              </Grid>
            </Grid>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleClose}>Cancel</Button>
            <Button type="submit" color="primary" variant="contained">
              {isEditMode ? "Update" : "Add"}
            </Button>
          </DialogActions>
        </form>
      </Dialog>
    </>
  );
}

